import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  Shield, FileText, Settings, LogOut, BarChart3,
  Save, Building, MapPin, Truck, Mail, Globe, Tag, Key, Send, TestTube
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '',
    nif_cif: '',
    street_name: '',
    street_number: '',
    piso: '',
    puerta: '',
    postal_code: '',
    municipio: '',
    comunidad: '',
    country: 'España',
    email: '',
    phone: '',
    // Service address
    service_street_name: '',
    service_street_number: '',
    service_piso: '',
    service_puerta: '',
    service_postal_code: '',
    service_municipio: '',
    service_comunidad: '',
    service_country: 'España',
    // SMTP Settings
    smtp_host: '',
    smtp_port: '587',
    smtp_user: '',
    smtp_password: '',
    smtp_from: ''
  });
  const [activeSection, setActiveSection] = useState('company');
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/settings`, { withCredentials: true });
        if (response.data && Object.keys(response.data).length > 0) {
          setSettings(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings`, settings, { withCredentials: true });
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="admin-settings">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-screen w-64 bg-[#0A0A0A] text-white flex flex-col">
        <div className="p-6">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0047FF] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">A</span>
            </div>
            <div>
              <span className="font-heading font-bold text-lg">Arreglatelo</span>
              <p className="text-xs text-[#A1A1AA]">Admin Panel</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-4">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <BarChart3 className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Dashboard</span>
          </Link>
          <Link to="/admin/warranties" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Shield className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Garantías</span>
          </Link>
          <Link to="/admin/invoices" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <FileText className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Facturas</span>
          </Link>
          <Link to="/admin/seo" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Globe className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">SEO & Contenido</span>
          </Link>
          <Link to="/admin/marketing" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Tag className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Marketing</span>
          </Link>
          <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 mb-1">
            <Settings className="w-5 h-5" />
            <span className="font-medium">Configuración</span>
          </Link>
        </nav>

        <div className="p-4 border-t border-white/10">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-white/5"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-4xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Configuración</h1>
              <p className="text-[#52525B]">Datos de la empresa y servicio técnico</p>
            </div>
            <Button 
              className="bg-[#0047FF] hover:bg-[#003BCC]"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-btn"
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
              <Save className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0047FF]"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Company Info */}
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#0047FF]/10 rounded-lg flex items-center justify-center">
                    <Building className="w-5 h-5 text-[#0047FF]" />
                  </div>
                  <h2 className="text-lg font-semibold">Datos de la Empresa (Facturas)</h2>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre de la Empresa</Label>
                    <Input
                      value={settings.company_name}
                      onChange={(e) => handleChange('company_name', e.target.value)}
                      className="mt-1"
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <Label>NIF/CIF</Label>
                    <Input
                      value={settings.nif_cif}
                      onChange={(e) => handleChange('nif_cif', e.target.value)}
                      className="mt-1 font-mono"
                      data-testid="nif-input"
                    />
                  </div>
                  <div>
                    <Label>Calle</Label>
                    <Input
                      value={settings.street_name}
                      onChange={(e) => handleChange('street_name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label>Número</Label>
                      <Input
                        value={settings.street_number}
                        onChange={(e) => handleChange('street_number', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Piso</Label>
                      <Input
                        value={settings.piso}
                        onChange={(e) => handleChange('piso', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Puerta</Label>
                      <Input
                        value={settings.puerta}
                        onChange={(e) => handleChange('puerta', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Código Postal</Label>
                    <Input
                      value={settings.postal_code}
                      onChange={(e) => handleChange('postal_code', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Municipio</Label>
                    <Input
                      value={settings.municipio}
                      onChange={(e) => handleChange('municipio', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Comunidad</Label>
                    <Input
                      value={settings.comunidad}
                      onChange={(e) => handleChange('comunidad', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>País</Label>
                    <Input
                      value={settings.country}
                      onChange={(e) => handleChange('country', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={settings.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="mt-1"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input
                      value={settings.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Service Address */}
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#10B981]/10 rounded-lg flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#10B981]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">Dirección del Servicio Técnico</h2>
                    <p className="text-sm text-[#52525B]">Destino para etiquetas de devolución</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Calle</Label>
                    <Input
                      value={settings.service_street_name}
                      onChange={(e) => handleChange('service_street_name', e.target.value)}
                      className="mt-1"
                      data-testid="service-street-input"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label>Número</Label>
                      <Input
                        value={settings.service_street_number}
                        onChange={(e) => handleChange('service_street_number', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Piso</Label>
                      <Input
                        value={settings.service_piso}
                        onChange={(e) => handleChange('service_piso', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Puerta</Label>
                      <Input
                        value={settings.service_puerta}
                        onChange={(e) => handleChange('service_puerta', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Código Postal</Label>
                    <Input
                      value={settings.service_postal_code}
                      onChange={(e) => handleChange('service_postal_code', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Municipio</Label>
                    <Input
                      value={settings.service_municipio}
                      onChange={(e) => handleChange('service_municipio', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Comunidad</Label>
                    <Input
                      value={settings.service_comunidad}
                      onChange={(e) => handleChange('service_comunidad', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>País</Label>
                    <Input
                      value={settings.service_country}
                      onChange={(e) => handleChange('service_country', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* SMTP Settings */}
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">Configuración SMTP (Email)</h2>
                    <p className="text-sm text-[#52525B]">Para enviar emails automáticos de garantías y facturas</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Servidor SMTP</Label>
                    <Input
                      value={settings.smtp_host}
                      onChange={(e) => handleChange('smtp_host', e.target.value)}
                      placeholder="smtp.tudominio.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Puerto</Label>
                    <Input
                      value={settings.smtp_port}
                      onChange={(e) => handleChange('smtp_port', e.target.value)}
                      placeholder="587"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Usuario SMTP</Label>
                    <Input
                      value={settings.smtp_user}
                      onChange={(e) => handleChange('smtp_user', e.target.value)}
                      placeholder="usuario@tudominio.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Contraseña SMTP</Label>
                    <Input
                      type="password"
                      value={settings.smtp_password}
                      onChange={(e) => handleChange('smtp_password', e.target.value)}
                      placeholder="••••••••"
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Email Remitente (From)</Label>
                    <Input
                      value={settings.smtp_from}
                      onChange={(e) => handleChange('smtp_from', e.target.value)}
                      placeholder="noreply@arreglatelo.es"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    <p>Proveedores comunes:</p>
                    <ul className="text-xs mt-1 space-y-0.5">
                      <li><strong>Gmail:</strong> smtp.gmail.com:587 (requiere App Password)</li>
                      <li><strong>Outlook:</strong> smtp.office365.com:587</li>
                      <li><strong>Ionos:</strong> smtp.ionos.es:587</li>
                    </ul>
                  </div>
                  <Button 
                    variant="outline"
                    onClick={async () => {
                      setTestingEmail(true);
                      try {
                        await axios.post(`${API_URL}/api/admin/test-email`, {}, { withCredentials: true });
                        toast.success('Email de prueba enviado a ' + settings.email);
                      } catch (error) {
                        toast.error('Error al enviar email de prueba');
                      } finally {
                        setTestingEmail(false);
                      }
                    }}
                    disabled={testingEmail || !settings.smtp_host}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {testingEmail ? 'Enviando...' : 'Enviar Email de Prueba'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminSettings;
